/* The placements read, over the real API. No X-Lumiq-Tenant header — hard rule 1.
 *
 * **One hook, because one screen needs it.** The Residency page is gone from the frontend, so the
 * policy read, the region registry and the federated view had no remaining caller. What survives is
 * the list of declared placements, which `ConnectionDetail` shows as each connection's region.
 *
 * Read-only, and there was never a write here: declaring a policy or placing a connection needs
 * `federation.residency:configure`, which only `admin` holds. The backend keeps all of it — every
 * federation route, permission and audit behaviour is untouched and still reachable by the API and
 * the CLI.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isPlacement } from '@/shared/api/generated/federation';
import type { Placement } from '@/shared/api/generated/federation';
import { getJson } from '@/shared/api/http';

export const PLACEMENTS_URL = '/v1/federation/placements';

function itemsOf(body: unknown, url: string): unknown[] {
  if (typeof body !== 'object' || body === null || !('items' in body)) {
    throw new Error(`${url} response has no items`);
  }
  const items: unknown = body.items;
  if (!Array.isArray(items)) throw new Error(`${url} items is not an array`);
  return items;
}

export function usePlacements(): UseQueryResult<readonly Placement[]> {
  return useQuery({
    queryKey: ['federation', 'placements'],
    retry: false,
    queryFn: async () => {
      const parsed: Placement[] = [];
      for (const item of itemsOf(await getJson(PLACEMENTS_URL), PLACEMENTS_URL)) {
        /* Refused whole rather than filtered: a placement silently missing from a residency screen
         * reads as a connection nobody has declared, which is the opposite of the truth. */
        if (!isPlacement(item))
          throw new Error('an item did not match the Placement schema');
        parsed.push(item);
      }
      return parsed;
    },
  });
}
